/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button, typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import SimplifiedMegaMenuWrapper from 'bundles/megamenu/components/SimplifiedMegaMenuWrapper';
import SearchBar from 'bundles/search-common/components/searchbar/SearchBar';
import { LOGGED_IN_HOME_PAGE_TYPE_TO_SLUG } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/page-header';

import 'css!bundles/page-header/components/desktop/__styles__/BrowseContentWrapper';

type Props = {
  className?: string;
  shouldShowCourseraPlusButton?: boolean;
};

const styles = {
  container: css`
    align-items: center;
  `,
  wrapper: css`
    display: flex;
    align-items: center;
  `,
  link: css`
    display: flex;
    min-width: max-content;
    max-height: min-content;
    margin-right: var(--cds-spacing-50);

    &.isCurrent {
      color: var(--cds-color-interactive-primary-hover);
    }

    ${typography2.bodySecondary}
    color: var(--cds-color-neutral-primary-weak);

    :hover,
    :focus,
    :active {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      text-decoration: none;
      color: var(--cds-color-interactive-primary-hover);
    }
  `,
};

const BrowseContentWrapper = (props: Props) => {
  const [searchInputIsFocused, setSearchInputIsFocused] = React.useState<boolean>(false);

  const onFocus = () => {
    setSearchInputIsFocused(true);
  };

  const onBlur = () => {
    setSearchInputIsFocused(false);
  };

  const { className: customClassName, shouldShowCourseraPlusButton } = props;
  const consumerNavRefresh = GrowthDiscoveryEpicClient.get('consumerNavRefresh');

  const track = useTracker();
  const location = useLocation();
  const isCurrent = location.pathname.split('/').pop() === 'my-learning';
  const { MY_LEARNING } = LOGGED_IN_HOME_PAGE_TYPE_TO_SLUG;

  return (
    <div
      css={styles.container}
      className={classNames('browse-content-wrapper horizontal-box', {
        [`${customClassName}`]: customClassName !== undefined,
      })}
    >
      <div className="MegamenuWrapperDiv" key="MegamenuWrapperDiv" id="MegamenuWrapperDiv" css={styles.wrapper}>
        <SimplifiedMegaMenuWrapper
          shouldShowCourseraPlusButton={shouldShowCourseraPlusButton}
          useCDSButton={consumerNavRefresh}
        />
      </div>

      {consumerNavRefresh && isAuthenticatedUser() && (
        <Button
          href={`/${MY_LEARNING}`}
          variant="ghost"
          css={styles.link}
          data-testid="nav-link-grid-item-my_learning"
          onClick={() => {
            track('click_page_tab', { pageTab: { name: 'lihp_my_learning' } });
          }}
          component="a"
          size="small"
          className={isCurrent ? 'isCurrent' : undefined}
        >
          {_t('My Learning')}
        </Button>
      )}
      <SearchBar
        key="SearchBar"
        isSearchPage={false}
        searchInputIsFocused={searchInputIsFocused}
        onFocus={onFocus}
        onBlur={onBlur}
        className={customClassName}
      />
    </div>
  );
};

export default BrowseContentWrapper;
